import styles from './license-dialog.module.scss';
import { useContext, useState } from 'react';
import { ValueFormatterParams } from 'ag-grid-community';
import { useMakeRequestHook } from '../../requestUtils';
import { useImmer } from 'use-immer';
import {
  booleanHeader, Checkbox, CheckboxLabel, Chip, CreateHeaders, dateFormatter, DatePicker, Dialog, DialogActions, DialogContent, DialogTitle, Divider, GrayOutlinedButton, HeaderSizeEnum, MenuItem, PrimaryButton,
  SecondaryButton,
  Select, SnackBarContext, textHeader, Typography
} from '@frontend-workspace/react-shared-lib';
import { IPersonsOnLicense } from '../../_models/persons-on-license';
import WebkuaTable from '../../webkua-table/webkua-table';
import { useTranslation } from 'react-i18next';
import { addMonths } from 'date-fns';
import { GetCurrentCompany } from '../../customer-context/customer-context';
import { Col, Row } from 'reactstrap';

/* eslint-disable-next-line */
export interface LicenseDialogProps {
  open: boolean,
  onClose: () => void,
  initialPersonsOnLicense: IPersonsOnLicense[],
  maxSeats: number,
  isNamedUser: boolean,
  licenseNumber: number,
  isTrialLicense?: boolean,
  lastLoanDatesMap?: Map<string, Date>
}

export function LicenseDialog(props: LicenseDialogProps) {
  const currentCompany = GetCurrentCompany();
  const makeRequest = useMakeRequestHook();
  const [actionName, setActionName] = useState<string>('');
  const [startDate, setStartDate] = useState<Date>(new Date());
  const [endDate, setEndDate] = useState<Date>(addMonths(new Date(), 1));
  const [sendEmail, setSendEmail] = useState<boolean>(true);
  const [isRemoveOldAccessesOpen, setIsRemoveOldAccessesOpen] = useState<boolean>(false);
  const [removeOldAccessDate, setRemoveOldAccessDate] = useState<Date>(addMonths(new Date(), -3));
  const [personsOnLicense, updatePersonsOnLicense] = useImmer<IPersonsOnLicense[]>(props.initialPersonsOnLicense);
  const currentLicenseAccesses = personsOnLicense.filter(person => person.hasAccess).length;

  const { setSnackbarMessage } = useContext(SnackBarContext);

  const { t } = useTranslation();

  const handleActionChange = (event: any) => setActionName(event.target.value);
  const handleSendEmail = (event: React.ChangeEvent<Element>, checked: boolean) => setSendEmail(checked);

  const handleGiveAllAccess = () => {
    setIsRemoveOldAccessesOpen(false);
    updatePersonsOnLicense(draft => {
      draft.forEach(person => person.hasAccess = true);
    });
  };

  const handleMakeAllCoordinators = () => {
    setIsRemoveOldAccessesOpen(false);
    updatePersonsOnLicense(draft => {
      draft.forEach(person => person.isCoordinator = true);
    });
  };

  const handleRemoveAllAccess = () => {
    setIsRemoveOldAccessesOpen(false);
    updatePersonsOnLicense(draft => {
      draft.forEach(person => person.hasAccess = false);
    });
  };

  const handleRemoveAllCoordinators = () => {
    setIsRemoveOldAccessesOpen(false);
    updatePersonsOnLicense(draft => {
      draft.forEach(person => person.isCoordinator = false);
    });
  };
  const handleRemoveOldAccessesOpen = () => {
    setIsRemoveOldAccessesOpen(true);
  }
  const handleRemoveOldAccesses = () => {
    updatePersonsOnLicense(draft => {
      draft.forEach(person => {
        const lastLoanDate = props.lastLoanDatesMap?.get(person.email) || new Date(0);
        if (lastLoanDate && lastLoanDate < removeOldAccessDate) {
          person.hasAccess = false;
        }
      });
    });
    setIsRemoveOldAccessesOpen(false);
  }
  const lastUsedRenderer = (params: ValueFormatterParams) => {
    const lastLoanDate = props.lastLoanDatesMap?.get(params.data.email) || new Date(0);

    return lastLoanDate.getTime() === new Date(0).getTime() ? "" : dateFormatter(lastLoanDate);
  };
  const lastUsedComparator = (email1: string, email2: string) => {
    const lastLoanDate1 = props.lastLoanDatesMap?.get(email1) || new Date(0);
    const lastLoanDate2 = props.lastLoanDatesMap?.get(email2) || new Date(0);

    return lastLoanDate1.getTime() - lastLoanDate2.getTime();
  };

  const handleAccessChange = () => {
    const licenseUserChanges = personsOnLicense.filter(p => {
      const initialPerson = props.initialPersonsOnLicense.find(ip => ip.email === p.email);
      return initialPerson?.hasAccess !== p.hasAccess;
    }).map(p => {
      return {
        licenseNumber: props.licenseNumber,
        giveAccess: p.hasAccess,
        email: p.email,
        isCoordinator: false
      }
    });
    const coordinatorChanges = personsOnLicense.filter(p => {
      const initialPerson = props.initialPersonsOnLicense.find(ip => ip.email === p.email);
      return initialPerson?.isCoordinator !== p.isCoordinator;
    }).map(p => {
      return {
        licenseNumber: props.licenseNumber,
        giveAccess: p.hasAccess,
        email: p.email,
        isCoordinator: true
      }
    });
    const url = props.isTrialLicense ? `licenses/action/changeTrialLicenseAccess` : `v2/licenses/action/changeLicenseAccess`;
    makeRequest(url, 'PUT', {
      activationDate: startDate,
      endDate: endDate,
      sendEmail,
      companyNumber: currentCompany?.number,
      accessChanges: licenseUserChanges.concat(coordinatorChanges)
    }).then(result => {
      //setSnackbarMessage({ content: t('standard.changesLicenseAccesses'), type: "info", });
      props.onClose();
    });
  };

  const onAccessChange = (userName: string, isCoordinatorChange: boolean, newValue: boolean) => {
    updatePersonsOnLicense(draft => {
      const changedPerson = draft.find(person => person.email === userName);
      if (changedPerson) {
        if (isCoordinatorChange) {
          changedPerson.isCoordinator = newValue;
        }
        if (!isCoordinatorChange) {
          if (props.isNamedUser && currentLicenseAccesses >= props.maxSeats && !changedPerson.hasAccess) {
            setSnackbarMessage({ content: t('license.canNotHaveMoreAccessesThanSeatsOnNamedUserLicenses'), type: "error", });
          } else changedPerson.hasAccess = newValue;
        }
      }
    });
  };

  const changeAccessRenderer = (params: ValueFormatterParams) => {
    const handleOnClick = (event: React.ChangeEvent<Element>, checked: boolean) => {
      onAccessChange(params.data.email, false, checked);
    }
    return <Checkbox
      checked={params.value}
      onChange={handleOnClick}
    />
  };

  const changeCoordinatorRenderer = (params: ValueFormatterParams) => {
    const handleOnClick = (event: React.ChangeEvent<Element>, checked: boolean) => {
      onAccessChange(params.data.email, true, checked);
    }
    return <Checkbox
      checked={params.value}
      onChange={handleOnClick}
    />
  }

  return (
    <Dialog
      open={props.open}
      onClose={props.onClose}
      maxWidth="xxl"
      fullWidth>
      <DialogTitle className='mb-4'>
        {t('standard.assignLicenseRights')}
      </DialogTitle>
      <DialogContent>
        <Typography
          variant="body6"
          component="div"
          className="mb-5">
          {`${t('license.numberOfUsersOnLicense')}: ${currentLicenseAccesses}/${props.maxSeats}`}
        </Typography>
        <div className="d-flex flex-row align-items-end flex-wrap">
          <div className='mb-3 mb-xxl-0 me-4'>
            <Select
              fullWidth
              label={t('standard.action')}
              value={actionName}
              onChange={handleActionChange}
              sx={{ width: 250 }}>
              <MenuItem value={0} onClick={handleGiveAllAccess}>{t('standard.giveEveryoneLicenseAccess')}</MenuItem>
              <MenuItem value={1} onClick={handleMakeAllCoordinators}>{t('standard.makeEveryoneACoordinator')}</MenuItem>
              <MenuItem value={2} onClick={handleRemoveAllAccess}>{t('standard.removeAllLicenseAccess')}</MenuItem>
              <MenuItem value={3} onClick={handleRemoveAllCoordinators}>{t('standard.removeAllCoordinators')}</MenuItem>
              <MenuItem value={4} onClick={handleRemoveOldAccessesOpen}>{t('customerProductListGridRenderer.removeOldAccesses')}</MenuItem>
            </Select>
          </div>
          <div className='mb-3 mb-xxl-0 me-4'>
            <DatePicker
              inputFormat="dd.MM.yyyy"
              label={t('standard.executionDate')}
              value={startDate}
              onChange={(newDateValue: any) => {
                setStartDate(newDateValue);
              }}
            />
          </div>
          {props.isTrialLicense && <div className='mb-3 mb-xxl-0 me-4'>
            <DatePicker
              inputFormat="dd.MM.yyyy"
              label={t('standard.stopDate')}
              value={endDate}
              onChange={(newDateValue: any) => {
                setEndDate(newDateValue);
              }}
            />
          </div>}
          <div className='mb-3 mb-xxl-0'>
            <CheckboxLabel
              label={t('standard.sendEmail')}
              checked={sendEmail}
              onChange={handleSendEmail}
            />
          </div>
        </div>
        {isRemoveOldAccessesOpen && <>
          <Divider className='my-3' />
          <Row className='mb-3'>
            <Col xs="auto">
              <DatePicker
                label={t('customerProductListGridRenderer.removeOldAccesses')}
                inputFormat="dd.MM.yyyy"
                value={removeOldAccessDate}
                onChange={(newDateValue: any) => {
                  setRemoveOldAccessDate(newDateValue);
                }}
              />
            </Col>
            <Col xs="auto" className='d-flex align-items-end mt-3 mt-md-0'>
              <SecondaryButton
                variant="contained"
                size='large'
                onClick={handleRemoveOldAccesses}
                className='minButtonWidth me-2'>
                OK
              </SecondaryButton>
              <GrayOutlinedButton
                variant="outlined"
                size='large'
                onClick={() => setIsRemoveOldAccessesOpen(false)}
                className='minButtonWidth'>
                {t('standard.cancel')}
              </GrayOutlinedButton>
            </Col>
          </Row>
        </>}
        <WebkuaTable
          headers={CreateHeaders([
            textHeader("firstName", t('standard.firstName'), HeaderSizeEnum.LARGE),
            textHeader("lastName", t('standard.lastName'), HeaderSizeEnum.LARGE),
            textHeader("email", t('standard.username'), HeaderSizeEnum.EXTRA_LARGE),
            textHeader("title", t('standard.title'), HeaderSizeEnum.LARGE),
            booleanHeader("isExternal", t('standard.external')),
            booleanHeader("hasAccess", t('standard.license'), {
              renderer: changeAccessRenderer
            }),
            textHeader("isCoordinator", t('standard.licensingCoordinator'), HeaderSizeEnum.LARGE, {
              renderer: changeCoordinatorRenderer
            }),
            textHeader("email", t('standard.lastUsed'), HeaderSizeEnum.LARGE, { renderer: lastUsedRenderer, comparator: lastUsedComparator })
          ])}
          data={personsOnLicense}
        />
      </DialogContent>
      <DialogActions>
        <PrimaryButton
          variant="contained"
          size='large'
          onClick={handleAccessChange}
          className='minButtonWidth'>
          OK
        </PrimaryButton>
        <GrayOutlinedButton
          variant="outlined"
          size='large'
          onClick={props.onClose}
          className='minButtonWidth'>
          {t('standard.cancel')}
        </GrayOutlinedButton>
      </DialogActions>
    </Dialog>
  );
}

export default LicenseDialog;